import React, { useCallback, useState } from 'react';
import { Button, Grid, IconButton, Typography } from '@mui/material';
import { WidgetProps } from '@rjsf/utils';
import PolarisIcon from '@/components/_common/PolarisIcon';
import { useAppSelector } from '@/redux/hooks';
import { FileManagerInventoryFile } from '@/redux/slices/slice_types';
import { selectCurrentPath } from '@/redux/selectors/fileManagerSelectors';
import Tooltip from '@/components/_common/Tooltip';
import FileManagerModal from '@/components/FileManager/FileManagerModal';
import { useToast } from '@/components/_common/Toast';

interface Props extends WidgetProps {
  value: string | undefined;
}

const FileSelectWidget: React.FC<Props> = (props) => {
  const { onChange, value, schema } = props;
  const path = useAppSelector(selectCurrentPath);
  const { error } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [modalSelectedFiles, setModalSelectedFiles] = useState<FileManagerInventoryFile[]>([]);

  const selectedFileName = value?.split('/').at(-1);

  const getNewValue = (file: FileManagerInventoryFile) => {
    return `/${path.join('/')}/${file.name}`;
  };

  const handleFileSelection = (item: FileManagerInventoryFile) => {
    if (schema.pattern && !getNewValue(item).match(new RegExp(schema.pattern))) {
      error(schema.message?.pattern || 'File path does not match the pattern');
      return;
    }

    if (modalSelectedFiles.includes(item)) {
      setModalSelectedFiles([]);
    } else {
      setModalSelectedFiles([item]);
    }
  };

  const openModal = useCallback(() => {
    setIsOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleSelect = () => {
    if (modalSelectedFiles[0]) {
      onChange(getNewValue(modalSelectedFiles[0]));
      setIsOpen(false);
    }
  };

  const clearSelection = () => {
    onChange(undefined);
  };

  const title = `Select ${props.schema.title}`;

  return (
    <>
      <Grid container alignItems={'center'}>
        <Grid item>
          <Typography sx={{ mr: 2 }}>{selectedFileName ? selectedFileName : title}</Typography>
        </Grid>
        <Grid item>
          {value ? (
            <Tooltip title="Clear">
              <IconButton onClick={clearSelection}>
                <PolarisIcon icon="delete" />
              </IconButton>
            </Tooltip>
          ) : (
            <Button variant="contained" endIcon={<PolarisIcon icon="search_folders" />} onClick={openModal}>
              Browse...
            </Button>
          )}
        </Grid>
      </Grid>

      <FileManagerModal
        open={isOpen}
        selectedFiles={modalSelectedFiles}
        modalTitle={title}
        canCreateFolder={false}
        footer={
          <Grid container justifyContent="end">
            <Button disabled={modalSelectedFiles.length < 1} variant="contained" sx={{ mt: 2 }} onClick={handleSelect}>
              Select
            </Button>
          </Grid>
        }
        onClose={closeModal}
        handleFileSelection={handleFileSelection}
      />
    </>
  );
};

export default FileSelectWidget;
